import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import Load from './Load';
import { Torneio } from '../services/torneioService';
import { StatusTorneio } from '../utils/StatusTorneio';
import {
  CRIAR_SEGUIDOR_MUTATION,
  DELETAR_SEGUIDOR_MUTATION,
  CriarSeguidorResponse,
  SEGUIDOR_USUARIO_TORNEIO,
  SeguidorUsuarioTorneioData,
  SeguidorUsuarioTorneioVars,
} from '../services/seguidorService';

const STATUS_LABELS: { value: StatusTorneio | null; label: string }[] = [
  { value: null, label: 'Todos' },
  { value: StatusTorneio.AGENDADO, label: 'Agendado' },
  { value: StatusTorneio.INSCRICOES_ABERTAS, label: 'Inscrições abertas' },
  { value: StatusTorneio.INSCRICOES_ENCERRADAS, label: 'Inscrições encerradas' },
  { value: StatusTorneio.ENCERRADO, label: 'Encerrado' },
  { value: StatusTorneio.CANCELADO, label: 'Cancelado' },
];

function formatarData(data: string | null | undefined): string {
  if (!data) return '–';
  return new Date(data).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface LeagueTournamentRowProps {
  torneio: Torneio;
  ligaId: string;
  isAtleta: boolean;
  isLiga: boolean;
  atletaId: string;
  userId: string | undefined;
}

const LeagueTournamentRow: React.FC<LeagueTournamentRowProps> = ({
  torneio,
  ligaId,
  isAtleta,
  isLiga,
  atletaId,
  userId,
}) => {
  const navigate = useNavigate();
  const [followId, setFollowId] = useState<string | null>(null);
  const [erroSeguir, setErroSeguir] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: seguidorData } = useQuery<SeguidorUsuarioTorneioData, SeguidorUsuarioTorneioVars>(
    SEGUIDOR_USUARIO_TORNEIO,
    {
      variables: { seguidorId: atletaId, seguidoTorneioId: torneio.id },
      skip: !atletaId || !torneio.id || !isAtleta,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    if (seguidorData?.seguidorPorSeguidorIdSeguidoTorneioId?.id) {
      setFollowId(seguidorData.seguidorPorSeguidorIdSeguidoTorneioId.id);
    } else {
      setFollowId(null);
    }
  }, [seguidorData]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const [criarSeguidor, { loading: loadingSeguir }] = useMutation<
    { criarSeguidor: CriarSeguidorResponse },
    { input: { seguidorId: string; seguidoAtletaId: null; seguidoTorneioId: string } }
  >(CRIAR_SEGUIDOR_MUTATION);
  const [deletarSeguidor, { loading: loadingDeletar }] = useMutation(DELETAR_SEGUIDOR_MUTATION);

  const handleSeguir = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!atletaId || !torneio.id) return;
    setErroSeguir('');
    try {
      if (followId) {
        await deletarSeguidor({ variables: { id: followId } });
        setFollowId(null);
      } else {
        setFollowId('temp');
        const { data } = await criarSeguidor({
          variables: {
            input: {
              seguidorId: atletaId,
              seguidoAtletaId: null,
              seguidoTorneioId: torneio.id,
            },
          },
        });
        if (data?.criarSeguidor?.id) setFollowId(data.criarSeguidor.id);
        else setFollowId(null);
      }
    } catch {
      setErroSeguir('Não foi possível atualizar.');
      setFollowId(followId === 'temp' ? null : followId);
    }
  };

  const isOrganizador =
    isLiga &&
    userId &&
    torneio.organizadores?.some((org) => org.usuario?.id === userId);
  const inscritosConfirmados =
    (torneio.totalInscricoes ?? 0) - (torneio.inscricoesNaoPagas ?? 0);

  return (
    <tr>
      <td className="align-middle">
        <img
          src={torneio.avatarUrl || '/assets/images/user.png'}
          alt={torneio.nome}
          className="rounded-circle"
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
      </td>
      <td className="align-middle">
        <Link
          to={`/tournamentsingle/${torneio.id}`}
          className="text-dark fw-600 text-decoration-none"
        >
          {torneio.nome}
        </Link>
        {torneio.username && (
          <Link
            to={`/tournamentsingle/${torneio.id}`}
            className="d-block font-xssss text-primary text-decoration-none"
          >
            @{torneio.username}
          </Link>
        )}
      </td>
      <td className="align-middle font-xssss">
        {formatarData(torneio.dataInscricaoInicio)} – {formatarData(torneio.dataInscricaoFim)}
      </td>
      <td className="align-middle font-xssss">
        {formatarData(torneio.dataInicio)} – {formatarData(torneio.dataFim)}
      </td>
      <td className="align-middle font-xssss">
        {torneio.totalCategorias ?? 0} categorias
        <br />
        {torneio.categoriasAtivas ?? 0} em andamento
      </td>
      <td className="align-middle font-xssss">
        {torneio.totalInscricoes ?? 0} inscritos
        <br />
        {inscritosConfirmados} confirmados
      </td>
      <td className="align-middle">
        <div className="dropdown" ref={dropdownRef}>
          <button
            id={`dropdown-actions-${torneio.id}`}
            className="btn btn-sm btn-outline-secondary dropdown-toggle"
            type="button"
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDropdownOpen((prev) => !prev);
            }}
          >
            Ações
          </button>
          <ul
            className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`}
            aria-labelledby={`dropdown-actions-${torneio.id}`}
          >
            <li>
              <Link
                className="dropdown-item"
                to={`/tournamentsingle/${torneio.id}`}
                onClick={() => setDropdownOpen(false)}
              >
                Visualizar
              </Link>
            </li>
            {isAtleta && atletaId && (
              <>
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={(e) => {
                      setDropdownOpen(false);
                      handleSeguir(e);
                    }}
                    disabled={loadingSeguir || loadingDeletar || followId === 'temp'}
                  >
                    {loadingSeguir || loadingDeletar
                      ? 'Aguarde...'
                      : followId
                      ? 'Deixar de seguir'
                      : 'Seguir'}
                  </button>
                </li>
                <li>
                  <Link
                    className="dropdown-item"
                    to={`/tournamentsingle/${torneio.id}`}
                    onClick={() => setDropdownOpen(false)}
                  >
                    Inscrever-se
                  </Link>
                </li>
              </>
            )}
            {isOrganizador && (
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate(`/liga/tournament/${torneio.id}/edit`);
                  }}
                >
                  Editar
                </button>
              </li>
            )}
          </ul>
        </div>
        {erroSeguir && (
          <div className="font-xssss text-danger mt-1">{erroSeguir}</div>
        )}
      </td>
    </tr>
  );
};

export interface LeagueTournamentsTabProps {
  ligaId: string;
  torneios: Torneio[];
  loading: boolean;
  error: string | null;
  statusFilter: StatusTorneio | null;
  onStatusFilterChange: (status: StatusTorneio | null) => void;
  isAtleta: boolean;
  isLiga: boolean;
  atletaId: string;
  userId: string | undefined;
}

const LeagueTournamentsTab: React.FC<LeagueTournamentsTabProps> = ({
  ligaId,
  torneios,
  loading,
  error,
  statusFilter,
  onStatusFilterChange,
  isAtleta,
  isLiga,
  atletaId,
  userId,
}) => {
  return (
    <div className="league-tournaments-tab">
      <div className="mb-3">
        <ul className="nav nav-pills flex-wrap gap-1" role="tablist">
          {STATUS_LABELS.map(({ value, label }) => (
            <li key={value ?? 'todos'} className="nav-item">
              <button
                type="button"
                className={`nav-link rounded-pill ${statusFilter === value ? 'active' : ''}`}
                onClick={() => onStatusFilterChange(value)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {loading && <Load />}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {!loading && !error && (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th scope="col" style={{ width: 50 }}></th>
                <th scope="col">Torneio</th>
                <th scope="col">Inscrições</th>
                <th scope="col">Período</th>
                <th scope="col">Categorias</th>
                <th scope="col">Inscritos</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {torneios.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    Nenhum torneio encontrado.
                  </td>
                </tr>
              ) : (
                torneios.map((t) => (
                  <LeagueTournamentRow
                    key={t.id}
                    torneio={t}
                    ligaId={ligaId}
                    isAtleta={isAtleta}
                    isLiga={isLiga}
                    atletaId={atletaId}
                    userId={userId}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeagueTournamentsTab;
